import { ResponseTransformer } from '@lemsar/api/transformers/response';
import { Injectable } from '@angular/core';
import { LemsarApiClient } from '@lemsar/api/client';
import { Observable } from 'rxjs/Observable';
import {
    Institution,
    InstitutionPictures,
    PopularVocational,
    PotentialSector,
    UploadInstitutionLogo,
    UploadInstitutionPicture,
    VocationalTraining,
} from '@lemsar/institution/domains/model/institution';
import { InstitutionBuilding } from '@lemsar/institution/domains/model/buildings';
import { UI } from '@shared/models/ui';
import { StoreData } from '@shared/module/common/model/store-data';
import { AuthStore } from '@lemsar/storage/stores/auth.store';
import { Collection } from '@lemsar/common/domains/interface/collection';
import { EquipmentInstitution } from '@lemsar/institution/domains/model/equipment';
import { ApplyPreWorkCard } from '../model/apply-pre-work-card';
import { Document } from '@lemsar/institution/domains/model/document';

/**
 *  @author     Arif Setianto <arifsetiantoo@gmail.com>
 *  @created    11/06/2018
 */
@Injectable()
export class InstitutionService {
    private _ui: StoreData<UI>;

    public constructor(private api: LemsarApiClient, private authStore: AuthStore) {
        this._ui = new StoreData<UI>(new UI());
    }

    public get ui$(): Observable<UI> {
        return this._ui.stream$;
    }

    public get ui(): UI {
        return this._ui.value;
    }

    public fetchAllPublished(parameter?: any): Observable<Collection<Institution>> {
        if (parameter.type) {
            if (parameter.type === '') {
                // parameter.type = ['government', 'private'];
            } else if (!Array.isArray(parameter.type)) {
                parameter.type = [parameter.type];
            }
        }
        if (parameter.province) {
            parameter.province = [parameter.province];
        }
        if (parameter.city) {
            parameter.city = [parameter.city];
        }
        const queryParams = !parameter
            ? { page: 1, limit: 9 }
            : Object.assign({ page: parameter.page || 1, limit: parameter.limit || 9 }, parameter);
        return (
            this.api
                .get<any>('published-institutions', queryParams)
                // .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(Institution));
                .pipe(ResponseTransformer.toCollection(Institution))
        );
    }

    public findTotalByCategory(parameter?: any): Observable<any[]> {
        if (parameter.province) {
            parameter.province = [parameter.province];
        }
        if (parameter.city) {
            parameter.city = [parameter.city];
        }
        return this.api.get<any>('institutions-category-summaries', parameter).pipe(ResponseTransformer.toData());
    }

    public fetchInstitution(id: string): Observable<Institution> {
        return this.api
            .get<Institution | any>('institutions/' + id)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public verifyInstitution(id: string, body: any = {}): Observable<Institution> {
        return this.api
            .patch<Institution | any>('institutions/' + id + '/verify', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public autoVerifyInstitution(id: string, body: any = {}): Observable<Institution> {
        return this.api
            .patch<Institution | any>('institutions/' + id + '/auto-verify', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public rejectInstitution(id: string, body: any = {}): Observable<void> {
        return this.api.patch<void>('institutions/' + id + '/reject', body).pipe(ResponseTransformer.toData());
    }

    public transferAccountInstitution(id: string, body: any = {}): Observable<void> {
        return this.api.post<void>('institutions/' + id + '/transfer-user', body).pipe(ResponseTransformer.toData());
    }

    public acceptTransferAccountInstitution(body: any = {}): Observable<void> {
        return this.api.post<void>('institutions/accept-transfer-user', body).pipe(ResponseTransformer.toData());
    }

    public blockInstitution(id: string, body: any = {}): Observable<Institution> {
        return this.api
            .patch<Institution | any>('institutions/' + id + '/block', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public unblockInstitution(id: string, body: any = {}): Observable<Institution> {
        return this.api
            .patch<Institution | any>('institutions/' + id + '/unblock', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public checkInstitution(id: string, body: any = {}): Observable<void> {
        return this.api
            .patch<void>('institutions/' + id + '/check', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public uncheckInstitution(id: string, body: any = {}): Observable<void> {
        return this.api
            .patch<void>('institutions/' + id + '/uncheck', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public updateInstitutionStep(id: string, body: any = {}): Observable<Institution> {
        return this.api
            .patch<Institution | any>('institutions/' + id + '/step', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public applyPreWorkCardInstitution(id: string, body: ApplyPreWorkCard): Observable<void> {
        const formData = new FormData();
        formData.append('bank_account_number', body.bank_account_number);
        formData.append('bank_account_name', body.bank_account_name);
        formData.append('bank_branch_name', body.bank_branch_name);
        formData.append('type', body.type);
        formData.append('document_number', body.document_number);
        body.documents.forEach((item, index) => {
            formData.append(`documents[${index}][id]`, item.id);
            formData.append(`documents[${index}][file]`, item.file);
        });

        return this.api
            .post<void>('institutions/' + id + '/apply-pre-work-card', formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public editPreWorkCardInstitution(id: string, body: ApplyPreWorkCard): Observable<void> {
        return this.api
            .patch<void>('institutions/' + id + '/edit-pre-work-card', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public editPreWorkCardInstitutionForUser(id: string, body: ApplyPreWorkCard): Observable<void> {
        return this.api
            .patch<void>('institutions/' + id + '/resubmit-pre-work-card', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public editDocumentInstitution(id: string, body: any): Observable<void> {
        const formData = new FormData();
        formData.append('file', body.file);

        return this.api
            .post<void>('pre-work-card-documents/' + id, formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Document));
    }

    public revokePreWorkCardInstitution(id: string, body: any = {}): Observable<void> {
        return this.api
            .patch<void>('institutions/' + id + '/revoke-pre-work-card', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public unRevokePreWorkCardInstitution(id: string): Observable<void> {
        return this.api
            .patch<void>('institutions/' + id + '/unrevoke-pre-work-card', null)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public acceptPreWorkCardInstitution(id: string): Observable<void> {
        return this.api
            .patch<void>('institutions/' + id + '/accept-pre-work-card', null)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public rejectPreWorkCardInstitution(id: string, body: any = {}): Observable<void> {
        return this.api
            .patch<void>('institutions/' + id + '/reject-pre-work-card', body)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public fetchAllInstitutions(params?: any): Observable<Collection<Institution>> {
        if (params.type) {
            params.type = [params.type];
        }
        const queryParams = !params ? { page: 1, limit: 20 } : Object.assign({ page: params.page || 1, limit: params.limit || 20 }, params);
        return this.api.get<any>('institutions', queryParams).pipe(ResponseTransformer.toCollection(Institution));
    }

    public fetchAppliedPreWorkCardInstitutions(params?: any): Observable<Collection<Institution>> {
        if (params.type) {
            params.type = [params.type];
        }
        const queryParams = !params ? { page: 1, limit: 20 } : Object.assign({ page: params.page || 1, limit: params.limit || 20 }, params);
        return this.api.get<any>('applied-pre-work-card-institutions', queryParams).pipe(ResponseTransformer.toCollection(Institution));
    }

    public fetchInstitutionByUser(id: string, params?: any): Observable<Collection<Institution>> {
        const queryParams = !params ? { page: 1, limit: 20 } : { page: params.page || 1, limit: params.limit || 20 };

        return this.api.get<any>('users/' + id + '/institutions', queryParams).pipe(ResponseTransformer.toCollection(Institution));
    }

    public fetchVerifiedInstitutionByUser(id: string, params?: any): Observable<Collection<Institution>> {
        const queryParams = !params ? { page: 1, limit: 20 } : { page: params.page || 1, limit: params.limit || 20 };

        return this.api.get<any>('users/' + id + '/verified-institutions', queryParams).pipe(ResponseTransformer.toCollection(Institution));
    }

    public fetchInstitutionByCity(id: string, params?: any): Observable<Collection<Institution>> {
        const queryParams = !params ? { page: 1, limit: 20 } : { page: params.page || 1, limit: params.limit || 20 };

        return this.api.get<any>('cities/' + id + '/institutions', queryParams).pipe(ResponseTransformer.toCollection(Institution));
    }

    public fetchInstitutionPicts(id: string): Observable<InstitutionPictures[]> {
        return this.api
            .get<any>('institutions/' + id + '/pictures')
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(InstitutionPictures));
    }

    public deleteInstitutionPict(id: string): Observable<void> {
        return this.api.delete<any>('institution-pictures/' + id).pipe(ResponseTransformer.toData());
    }

    public fetchInstitutionBuildings(id: string, params?: any): Observable<InstitutionBuilding[]> {
        const queryParams = !params
            ? {} // { page: 1, limit: 20 }
            : {
                  //   page: params.page || 1,
                  //   limit: params.limit || 20,
                  type: params.type || '',
                  // group: params.group || [],
              };
        return this.api
            .get<any>('institutions/' + id + '/building-galleries', queryParams)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(InstitutionBuilding));
    }

    public fetchInstitutionEquipments(id: string, params?: any): Observable<EquipmentInstitution[]> {
        const queryParams = !params
            ? {}
            : {
                  category: params.category || '',
              };
        return this.api
            .get<any>('institutions/' + id + '/equipment-institution-galleries', queryParams)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(EquipmentInstitution));
    }

    public createInstitutionGovernment(data: any): Observable<any> {
        const formData: any = new FormData();

        formData.append('about', data.about);

        formData.append('username', data.username);
        formData.append('email', data.email);
        formData.append('password', data.password);
        formData.append('name', data.name);
        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        if (data.location_long) {
            formData.append('location_long', data.location_long);
        }

        if (data.location_lat) {
            formData.append('location_lat', data.location_lat);
        }

        formData.append('institution_type', data.institution_type);
        formData.append('postal_code', data.postal_code);
        formData.append('condition', data.condition);
        formData.append('accreditation', data.accreditation);
        formData.append('area', data.area);
        formData.append('since', data.since.getFullYear());

        formData.append('ministry', data.ministry);
        formData.append('type', data.type);
        formData.append('echelon', data.echelon);
        formData.append('sotk_number', data.sotk_number);

        if (data.sotk_file) {
            formData.append('sotk_file', data.sotk_file, data.sotk_file.name);
        }

        return this.api
            .post<any>('users/' + this.authStore.user.id + '/government-institutions', formData)
            .pipe(ResponseTransformer.toData());
    }

    public updateInstitutionGovernment(data: any, institutionId: string): Observable<any> {
        const formData: any = new FormData();

        formData.append('about', data.about);

        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        if (data.location_long) {
            formData.append('location_long', data.location_long);
        }

        if (data.location_lat) {
            formData.append('location_lat', data.location_lat);
        }

        formData.append('institution_type', data.institution_type);
        formData.append('postal_code', data.postal_code);
        formData.append('condition', data.condition);
        formData.append('accreditation', data.accreditation);
        formData.append('expired_at', data.expired_at);
        formData.append('area', data.area);
        formData.append('since', data.since.getFullYear().toString());

        formData.append('ministry', data.ministry);
        formData.append('type', data.type);
        formData.append('echelon', data.echelon);
        formData.append('sotk_number', data.sotk_number);

        if (data.sotk_file) {
            formData.append('sotk_file', data.sotk_file, data.sotk_file.name);
        }

        if (data.sectors) {
            for (const sector of data.sectors) {
                formData.append('sectors[]', sector.id);
            }
        }

        if (data.vocational_trainings) {
            for (const vocational of data.vocational_trainings) {
                formData.append('vocational_trainings[]', vocational.id);
            }
        }

        if (data.popular_vocationals) {
            for (const vocational of data.popular_vocationals) {
                formData.append('popular_vocationals[]', vocational.id);
            }
        }

        return this.api
            .post<any>('users/' + this.authStore.user.id + '/government-institutions/' + institutionId, formData)
            .pipe(ResponseTransformer.toData());
    }

    public transformInstitutionGovernment(institutionId: string, data: any): Observable<any> {
        const formData: any = new FormData();

        formData.append('institution_type', data.institution_type);
        formData.append('ministry', data.ministry);
        formData.append('type', data.type);
        formData.append('echelon', data.echelon);
        formData.append('sotk_number', data.sotk_number);

        if (data.sotk_file) {
            formData.append('sotk_file', data.sotk_file, data.sotk_file.name);
        }

        return this.api
            .post<any>('institutions/' + institutionId + '/government-institutions', formData)
            .pipe(ResponseTransformer.toData());
    }

    public createInstitutionPrivate(data: any): Observable<any> {
        const formData: any = new FormData();

        formData.append('about', data.about);

        formData.append('username', data.username);
        formData.append('email', data.email);
        formData.append('password', data.password);
        formData.append('name', data.name);
        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        if (data.location_long) {
            formData.append('location_long', data.location_long);
        }

        if (data.location_lat) {
            formData.append('location_lat', data.location_lat);
        }

        formData.append('institution_type', data.institution_type);
        formData.append('postal_code', data.postal_code);
        formData.append('condition', data.condition);
        formData.append('accreditation', data.accreditation);
        formData.append('area', data.area);
        formData.append('since', data.since.getFullYear());
        formData.append('license_number', data.license_number);

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);

        return this.api.post<any>('users/' + this.authStore.user.id + '/private-institutions', formData).pipe(ResponseTransformer.toData());
    }

    public updateInstitutionPrivate(data: any, institutionId: string): Observable<any> {
        const formData: any = new FormData();

        formData.append('about', data.about);

        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        if (data.location_long) {
            formData.append('location_long', data.location_long);
        }

        if (data.location_lat) {
            formData.append('location_lat', data.location_lat);
        }

        formData.append('institution_type', data.institution_type);
        formData.append('condition', data.condition);
        formData.append('accreditation', data.accreditation);
        formData.append('expired_at', data.expired_at);
        formData.append('area', data.area);
        formData.append('postal_code', data.postal_code);
        formData.append('since', data.since.getFullYear().toString());
        formData.append('license_number', data.license_number);

        if (data.private_type) {
            formData.append('type', data.private_type);
        }

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);

        if (data.sectors) {
            for (const sector of data.sectors) {
                formData.append('sectors[]', sector.id);
            }
        }

        if (data.vocational_trainings) {
            for (const vocational of data.vocational_trainings) {
                formData.append('vocational_trainings[]', vocational.id);
            }
        }

        if (data.popular_vocationals) {
            for (const vocational of data.popular_vocationals) {
                formData.append('popular_vocationals[]', vocational.id);
            }
        }

        return this.api
            .post<any>('users/' + this.authStore.user.id + '/private-institutions/' + institutionId, formData)
            .pipe(ResponseTransformer.toData());
    }

    public transformInstitutionPrivate(institutionId: string, data: any): Observable<any> {
        const formData: any = new FormData();

        formData.append('institution_type', data.institution_type);
        formData.append('license_number', data.license_number);

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);

        return this.api.post<any>('institutions/' + institutionId + '/private-institutions', formData).pipe(ResponseTransformer.toData());
    }

    public createInstitutionIndustrial(data: any): Observable<any> {
        const formData: any = new FormData();

        formData.append('about', data.about);

        formData.append('username', data.username);
        formData.append('email', data.email);
        formData.append('password', data.password);
        formData.append('name', data.name);
        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        if (data.location_long) {
            formData.append('location_long', data.location_long);
        }

        if (data.location_lat) {
            formData.append('location_lat', data.location_lat);
        }

        formData.append('postal_code', data.postal_code);
        formData.append('condition', data.condition);
        formData.append('accreditation', data.accreditation);
        formData.append('area', data.area);
        formData.append('since', data.since.getFullYear());
        formData.append('license_number', data.license_number);

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);
        formData.append('wlkp_code', data.wlkp_code);

        return this.api
            .post<any>('users/' + this.authStore.user.id + '/industrial-institutions', formData)
            .pipe(ResponseTransformer.toData());
    }

    public updateInstitutionIndustrial(data: any, institutionId: string): Observable<any> {
        const formData: any = new FormData();

        formData.append('about', data.about);

        formData.append('institution_name', data.institution_name);
        formData.append('owner', data.owner);
        formData.append('address', data.address);
        formData.append('phone', data.phone);

        if (data.fax) {
            formData.append('fax', data.fax);
        }

        formData.append('institution_email', data.institution_email);

        if (data.website) {
            formData.append('website', data.website);
        }

        if (data.location_long) {
            formData.append('location_long', data.location_long);
        }

        if (data.location_lat) {
            formData.append('location_lat', data.location_lat);
        }

        formData.append('condition', data.condition);
        formData.append('accreditation', data.accreditation);
        formData.append('expired_at', data.expired_at);
        formData.append('area', data.area);
        formData.append('postal_code', data.postal_code);
        formData.append('since', data.since.getFullYear().toString());
        formData.append('license_number', data.license_number);

        if (data.private_type) {
            formData.append('type', data.private_type);
        }

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);

        if (data.sectors) {
            for (const sector of data.sectors) {
                formData.append('sectors[]', sector.id);
            }
        }

        if (data.vocational_trainings) {
            for (const vocational of data.vocational_trainings) {
                formData.append('vocational_trainings[]', vocational.id);
            }
        }

        if (data.popular_vocationals) {
            for (const vocational of data.popular_vocationals) {
                formData.append('popular_vocationals[]', vocational.id);
            }
        }

        return this.api
            .post<any>('users/' + this.authStore.user.id + '/industrial-institutions/' + institutionId, formData)
            .pipe(ResponseTransformer.toData());
    }

    public transformInstitutionIndustrial(institutionId: string, data: any): Observable<any> {
        const formData: any = new FormData();

        formData.append('license_number', data.license_number);
        formData.append('wlkp_code', data.wlkp_code);

        if (data.license_file) {
            formData.append('license_file', data.license_file, data.license_file.name);
        }

        formData.append('tax_number', data.tax_number);

        if (data.tax_file) {
            formData.append('tax_file', data.tax_file, data.tax_file.name);
        }

        formData.append('pic_identity_number', data.pic_identity_number);

        return this.api
            .post<any>('institutions/' + institutionId + '/industrial-institutions', formData)
            .pipe(ResponseTransformer.toData());
    }

    public fetchInstitutionPictures(id: string): Observable<InstitutionPictures[]> {
        return this.api
            .get<any>('institutions/' + id + '/pictures')
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(InstitutionPictures));
    }

    public uploadInstitutionPicture(value: UploadInstitutionPicture, id: string): Observable<InstitutionPictures> {
        const formData: any = new FormData();

        formData.append('image', value.image, value.image.name);

        return this.api
            .post<any>('institutions/' + id + '/pictures', formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(InstitutionPictures));
    }

    public uploadInstitutionLogo(value: UploadInstitutionLogo, id: string): Observable<Institution> {
        const formData: any = new FormData();

        formData.append('logo', value.logo, value.logo.name);

        return this.api
            .post<any>('institutions/' + id + '/logos', formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public fetchInstitutionSectors(id: string): Observable<PotentialSector[]> {
        return this.api
            .get<any>('institutions/' + id + '/sectors')
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(PotentialSector));
    }

    public fetchInstitutionPopularVocationals(id: string): Observable<PopularVocational[]> {
        return this.api
            .get<any>('institutions/' + id + '/popular-vocationals')
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(PopularVocational));
    }

    public fetchInstitutionVocationalTrainings(id: string): Observable<VocationalTraining[]> {
        return this.api
            .get<any>('institutions/' + id + '/vocational-trainings')
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(VocationalTraining));
    }

    public updateCover(institutionId: string, galleryId: string, value: any): Observable<any> {
        return this.api
            .patch<any>('institutions/' + institutionId + '/pictures/' + galleryId + '/set-cover', value)
            .pipe(ResponseTransformer.toData());
    }

    public fetchPendingInstitutionSummary(cityId?: string): Observable<any> {
        const queryParams = cityId ? { city: cityId } : {};

        return this.api.get<any>('pending-institutions-summaries', queryParams).pipe(ResponseTransformer.toData());
    }

    public fetchInstitutionDocuments(id: string): Observable<Collection<Document>> {
        return this.api.get<any>('institutions/' + id + '/pre-work-card-documents').pipe(ResponseTransformer.toCollection(Document));
    }

    public fetchAppliedJkpInstitutions(params?: any): Observable<Collection<Institution>> {
        if (params.type) {
            params.type = [params.type];
        }
        const queryParams = !params ? { page: 1, limit: 20 } : Object.assign({ page: params.page || 1, limit: params.limit || 20 }, params);
        return this.api.get<any>('applied-jkp-institutions', queryParams).pipe(ResponseTransformer.toCollection(Institution));
    }

    public checkVerifiedInstitution(id: string): Observable<boolean> {
        return this.api.get<void>('institutions/' + id + '/check-verified', {}).pipe(ResponseTransformer.toData());
    }
}



// WEBPACK FOOTER //
// ./src/lemsar/institution/domains/services/institution.service.ts